import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, TextField, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Paper, Chip, Divider, Grid, Alert, CircularProgress
} from '@mui/material';
import { Print, Download } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';

// Use axios with auth headers from localStorage
const api = {
  get:    (url, cfg={}) => axios.get(`${process.env.REACT_APP_API_URL}${url}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  post:   (url, data, cfg={}) => axios.post(`${process.env.REACT_APP_API_URL}${url}`, data, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  put:    (url, data, cfg={}) => axios.put(`${process.env.REACT_APP_API_URL}${url}`, data, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  delete: (url, cfg={}) => axios.delete(`${process.env.REACT_APP_API_URL}${url}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
};
const API_URL = process.env.REACT_APP_API_URL?.replace('/api','') || '';

function gradeColor(letter) {
  if (!letter) return 'default';
  if (['A+','A','A-'].includes(letter)) return 'success';
  if (['B+','B','B-'].includes(letter)) return 'primary';
  if (['C+','C','C-'].includes(letter)) return 'warning';
  return 'error';
}

function classificationColor(c) {
  if (c?.includes('Summa'))  return '#7b1fa2';
  if (c?.includes('Magna'))  return '#1565c0';
  if (c?.includes('Cum'))    return '#2e7d32';
  if (c === 'Distinction')   return '#c9952a';
  if (c === 'Merit')         return '#0f2b4a';
  return '#444';
}

export default function Transcript() {
  const { enqueueSnackbar } = useSnackbar();
  const [students, setStudents] = useState([]);
  const [selectedStudent, setStudent] = useState('');
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/students').then(r => setStudents(r.data.data || []));
  }, []);

  const load = async () => {
    if (!selectedStudent) return;
    setLoading(true);
    try {
      const r = await api.get(`/tertiary/transcript/${selectedStudent}`);
      setTranscript(r.data.data);
    } catch (e) {
      enqueueSnackbar(e.response?.data?.message || 'Failed to load transcript', { variant:'error' });
      setTranscript(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (selectedStudent) load(); }, [selectedStudent]);

  const print = () => {
    const token = localStorage.getItem('sms_token');
    const slug  = localStorage.getItem('sms_slug');
    window.open(`${API_URL}/api/tertiary/transcript/${selectedStudent}/pdf?token=${token}&slug=${slug}`, '_blank');
  };

  const gpa = parseFloat(transcript?.cumulative_gpa || 0);

  return (
    <Box sx={{ p:3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Academic Transcript</Typography>
          <Typography variant="body2" color="text.secondary">Official academic record with GPA and credit hours</Typography>
        </Box>
        {transcript && (
          <Button variant="contained" startIcon={<Print />} onClick={print} sx={{ bgcolor:'#0f2b4a' }}>
            Print / Save PDF
          </Button>
        )}
      </Stack>

      <TextField select size="small" label="Select Student" value={selectedStudent}
        onChange={e => setStudent(e.target.value)} sx={{ minWidth:300, mb:3 }}>
        <MenuItem value="">— Search student —</MenuItem>
        {students.map(s => (
          <MenuItem key={s.id} value={s.id}>
            {s.first_name} {s.last_name} {s.admission_number ? `(${s.admission_number})` : ''}
          </MenuItem>
        ))}
      </TextField>

      {loading && <Box sx={{ textAlign:'center', py:4 }}><CircularProgress /></Box>}

      {!loading && transcript && (
        <Box id="transcript-body">
          {/* Header */}
          <Paper variant="outlined" sx={{ borderRadius:2, overflow:'hidden', mb:3 }}>
            <Box sx={{ bgcolor:'#0f2b4a', color:'white', p:3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h6" fontWeight={800}>{transcript.school?.school_name || 'Institution'}</Typography>
                  <Typography variant="body2" sx={{ opacity:0.7 }}>Official Academic Transcript</Typography>
                </Box>
                <Box sx={{ textAlign:'right' }}>
                  <Typography variant="caption" sx={{ opacity:0.6 }}>Generated</Typography>
                  <Typography variant="body2">{new Date(transcript.generated_at).toLocaleDateString('en-ZA', { year:'numeric', month:'long', day:'numeric' })}</Typography>
                </Box>
              </Stack>
            </Box>

            {/* Student info */}
            <Box sx={{ p:3, bgcolor:'#f8f9fc' }}>
              <Grid container spacing={2}>
                {[
                  { label:'Full Name', value: `${transcript.student.first_name} ${transcript.student.last_name}` },
                  { label:'Student Number', value: transcript.student.student_number || '—' },
                  { label:'Programme', value: transcript.student.programme_name },
                  { label:'Programme Code', value: transcript.student.programme_code },
                  { label:'Programme Type', value: transcript.student.programme_type },
                  { label:'Enrolment Date', value: transcript.student.enrolment_date ? new Date(transcript.student.enrolment_date).toLocaleDateString() : '—' },
                  { label:'Status', value: transcript.student.enrolment_status },
                  { label:'Expected Graduation', value: transcript.student.expected_graduation ? new Date(transcript.student.expected_graduation).toLocaleDateString() : '—' },
                ].map(f => (
                  <Grid item xs={6} sm={3} key={f.label}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} fontSize={10} textTransform="uppercase">{f.label}</Typography>
                    <Typography fontWeight={600} fontSize={13}>{f.value}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* GPA Summary */}
            <Box sx={{ p:3, borderTop:'1px solid #e0e0e0' }}>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontSize={10} fontWeight={700} textTransform="uppercase">Cumulative GPA</Typography>
                  <Typography variant="h3" fontWeight={900} color={gpa >= 3.0 ? 'success.main' : gpa >= 2.0 ? 'warning.main' : 'error.main'}>
                    {gpa.toFixed(3)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">out of 4.000</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontSize={10} fontWeight={700} textTransform="uppercase">Credits Earned</Typography>
                  <Typography variant="h3" fontWeight={900} color="#0f2b4a">
                    {transcript.total_credits_earned}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">of {transcript.student.required_credits} required</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" fontSize={10} fontWeight={700} textTransform="uppercase">Classification</Typography>
                  <Typography variant="h5" fontWeight={900} sx={{ color: classificationColor(transcript.classification) }}>
                    {transcript.classification}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Semester breakdown */}
          {transcript.semesters.map((sem, si) => (
            <Box key={si} sx={{ mb:3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb:1 }}>
                <Typography fontWeight={700} fontSize={15} sx={{ color:'#0f2b4a' }}>
                  {sem.year} — Semester {sem.semester}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Semester GPA: <strong>{sem.gpa}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Credits: <strong>{sem.credits}</strong>
                  </Typography>
                </Stack>
              </Stack>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius:2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor:'#f0f4f8' }}>
                      {['Course Code','Course Name','Credits','CA','Exam','Total','Grade','Points','Status'].map(h => (
                        <TableCell key={h} sx={{ fontWeight:700, fontSize:11 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sem.courses.map((c, ci) => (
                      <TableRow key={ci} hover>
                        <TableCell><Typography fontFamily="monospace" fontSize={12} fontWeight={700}>{c.course_code}</Typography></TableCell>
                        <TableCell><Typography fontSize={13}>{c.course_name}</Typography></TableCell>
                        <TableCell align="center">{c.credits}</TableCell>
                        <TableCell align="center">{c.continuous_assessment ?? '—'}</TableCell>
                        <TableCell align="center">{c.final_exam ?? '—'}</TableCell>
                        <TableCell align="center"><Typography fontWeight={700}>{c.total_mark ?? '—'}</Typography></TableCell>
                        <TableCell><Chip label={c.letter_grade || '—'} size="small" color={gradeColor(c.letter_grade)} /></TableCell>
                        <TableCell align="center">{c.grade_points ?? '—'}</TableCell>
                        <TableCell><Chip label={c.status || '—'} size="small" color={c.status === 'pass' ? 'success' : 'error'} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}

          {/* Signature block */}
          <Paper variant="outlined" sx={{ p:3, borderRadius:2, mt:2 }}>
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <Box sx={{ borderTop:'1px solid #333', pt:1, mt:4 }}>
                  <Typography variant="caption" color="text.secondary">Registrar Signature & Date</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ borderTop:'1px solid #333', pt:1, mt:4 }}>
                  <Typography variant="caption" color="text.secondary">Official Stamp</Typography>
                </Box>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ mt:2, display:'block' }}>
              This is an official academic transcript issued by {transcript.school?.school_name}. 
              Any alteration renders this document invalid.
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
